import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import AuthContext from './authContext';
import { subscribeCountsInvalidate } from '../util/countInvalidationBus';

// CountContext is the single source of truth for sidebar count badges.
//
// Counts are push-driven via Socket.io: the backend emits
// `counts:invalidate { keys }` (initially on connect for every key the user has
// rights for, and on every relevant mutation thereafter). Invalidated keys are
// collected for a short window and then fetched **in one bundled request**
// against `GET /api/v1/counts?keys=...` — previously every key did its own
// round-trip, so a login or reconnect produced ~25 requests (each preceded by a
// CORS preflight) instead of one.
//
// Two ways to register a key:
//   - registerCount(key)              — the normal case: the key is fetched as
//                                       part of the bundle
//   - registerInterval(key, callback) — a key with its own fetch logic (the
//                                       call-center counter needs the selected
//                                       agent's skills). Such a key is fetched
//                                       through its callback and stays out of
//                                       the bundle.
//
// Both are ref-counted, so a dashboard widget and a sidebar badge may hold the
// same key without fetching it twice.
//
// API surface:
//   - registerCount(key) / unregisterCount(key)
//   - registerInterval(key, callback) / unregisterInterval(key)
//   - setBundledFetcher(fetcher)  — injected by AuthContext, which owns the
//                                   auth headers; `fetcher(keys)` resolves to
//                                   `{ [key]: number }`
//   - refreshCount(key) / refreshCounts(keys) — queue a refetch
//   - setCount(key, value)
//   - counts — current values { [key]: number }

const CountContext = React.createContext({
  counts: {},
  setCount: () => {},
  registerCount: () => {},
  unregisterCount: () => {},
  registerInterval: () => {},
  unregisterInterval: () => {},
  setBundledFetcher: () => {},
  refreshCount: () => {},
  refreshCounts: () => {},
});

// Collect window for refetches. Long enough that the burst of registrations on
// login and the initial `counts:invalidate` collapse into a single request.
const REFETCH_DEBOUNCE_MS = 150;

export const CountContextProvider = ({ children }) => {
  const [counts, setCounts] = useState({});
  const refCounters = useRef({}); // { [key]: number } — registration ref-count
  const fetchCallbacksRef = useRef({}); // { [key]: () => void } — custom fetchers
  const bundledKeysRef = useRef(new Set()); // keys served by the bundled endpoint
  const bundledFetcherRef = useRef(null); // (keys) => Promise<{ [key]: number }>
  const pendingRefetchRef = useRef(new Set()); // keys queued for the next flush
  const refetchTimerRef = useRef(null);
  const authCtx = useContext(AuthContext);

  const setCount = useCallback((key, value) => {
    setCounts(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const setBundledFetcher = useCallback(fetcher => {
    bundledFetcherRef.current = typeof fetcher === 'function' ? fetcher : null;
  }, []);

  const runRegisteredFetch = useCallback(key => {
    const cb = fetchCallbacksRef.current[key];
    if (typeof cb === 'function') {
      try {
        cb();
      } catch (err) {
        console.error(`countContext: fetch for "${key}" failed:`, err);
      }
    }
  }, []);

  // Fetch every queued key. Keys with a custom fetcher run their own callback;
  // everything else goes out as one bundled request, so a burst of
  // invalidations costs a single round-trip no matter how many keys it touched.
  const flushPendingRefetches = useCallback(() => {
    refetchTimerRef.current = null;
    const keys = Array.from(pendingRefetchRef.current);
    pendingRefetchRef.current = new Set();
    if (keys.length === 0) return;

    const bundled = [];
    for (const key of keys) {
      if (typeof fetchCallbacksRef.current[key] === 'function') {
        runRegisteredFetch(key);
      } else if (bundledKeysRef.current.has(key)) {
        bundled.push(key);
      }
    }

    if (bundled.length === 0 || !bundledFetcherRef.current) return;

    const load = async () => {
      try {
        const values = await bundledFetcherRef.current(bundled);
        if (!values) return;
        setCounts(prev => {
          const next = { ...prev };
          for (const key of bundled) {
            // A key the server withheld (missing right, failed resolver) keeps
            // its previous value rather than flashing to zero.
            if (typeof values[key] === 'number') next[key] = values[key];
          }
          return next;
        });
      } catch (err) {
        console.error('countContext: bundled count fetch failed:', err);
      }
    };
    load();
  }, [runRegisteredFetch]);

  const queueRefetch = useCallback(
    key => {
      if (!key) return;
      pendingRefetchRef.current.add(key);
      if (refetchTimerRef.current) return;
      refetchTimerRef.current = setTimeout(flushPendingRefetches, REFETCH_DEBOUNCE_MS);
    },
    [flushPendingRefetches]
  );

  // Registers a key that is fetched as part of the bundle. The first
  // registration queues it, so all keys registered in the same render collapse
  // into one request instead of firing immediately one by one.
  const registerCount = useCallback(
    key => {
      if (!key) return;
      bundledKeysRef.current.add(key);
      refCounters.current[key] = (refCounters.current[key] || 0) + 1;
      if (refCounters.current[key] === 1) queueRefetch(key);
    },
    [queueRefetch]
  );

  const unregisterCount = useCallback(key => {
    if (!key || !refCounters.current[key]) return;
    refCounters.current[key] -= 1;
    if (refCounters.current[key] <= 0) {
      delete refCounters.current[key];
      bundledKeysRef.current.delete(key);
      pendingRefetchRef.current.delete(key);
    }
  }, []);

  // Registers a key that brings its own fetch logic. Kept for counters the
  // bundled endpoint cannot serve on its own (the call-center counter needs the
  // selected agent's skills).
  const registerInterval = useCallback((key, callback) => {
    if (!key || typeof callback !== 'function') return;

    fetchCallbacksRef.current[key] = callback;

    refCounters.current[key] = (refCounters.current[key] || 0) + 1;
    if (refCounters.current[key] === 1) {
      // Run once so the UI has a value immediately, without waiting for the
      // next server-driven invalidate.
      try {
        callback();
      } catch (err) {
        console.error(`countContext: initial fetch for "${key}" failed:`, err);
      }
    }
  }, []);

  const unregisterInterval = useCallback(key => {
    if (!key || !refCounters.current[key]) return;
    refCounters.current[key] -= 1;
    if (refCounters.current[key] <= 0) {
      delete refCounters.current[key];
      delete fetchCallbacksRef.current[key];
      pendingRefetchRef.current.delete(key);
    }
  }, []);

  const refreshCount = useCallback(
    key => {
      if (!key) return;
      queueRefetch(key);
    },
    [queueRefetch]
  );

  const refreshCounts = useCallback(
    keys => {
      if (!Array.isArray(keys)) return;
      for (const key of keys) queueRefetch(key);
    },
    [queueRefetch]
  );

  useEffect(() => {
    if (!authCtx.token) {
      refCounters.current = {};
      fetchCallbacksRef.current = {};
      bundledKeysRef.current = new Set();
      pendingRefetchRef.current = new Set();
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = null;
      }
      setCounts({});
    }
  }, [authCtx.token]);

  useEffect(() => {
    const unsubscribe = subscribeCountsInvalidate(payload => {
      const keys =
        payload && Array.isArray(payload.keys) ? payload.keys.filter(k => typeof k === 'string') : [];
      for (const key of keys) {
        queueRefetch(key);
      }
    });
    return unsubscribe;
  }, [queueRefetch]);

  useEffect(() => {
    return () => {
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = null;
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      counts,
      setCount,
      registerCount,
      unregisterCount,
      registerInterval,
      unregisterInterval,
      setBundledFetcher,
      refreshCount,
      refreshCounts,
    }),
    [
      counts,
      setCount,
      registerCount,
      unregisterCount,
      registerInterval,
      unregisterInterval,
      setBundledFetcher,
      refreshCount,
      refreshCounts,
    ]
  );

  return <CountContext.Provider value={contextValue}>{children}</CountContext.Provider>;
};

export default CountContext;
